import React, { Fragment, Suspense, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { messages } from '../../messages';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerContentWarningAndBrandSection } from '../ai-answer-footer/ai-answer-content-warning-and-brand-section';
import { NLPSearchResultFormat } from '../nlp-search-result-format';
import { onAIAnswerLoadingDialogShown } from '../on-ai-answer-loading-dialog-shown';
import { Collapse } from '../smart-answer-card/collapse';
import { AIAnswerLoadingStream } from './ai-answer-loading-stream';
import { AIAnswerSearchLoadingBorder } from './ai-answer-search-loading-border';
import GeneratingAnimation from './generating-animation';
import { GeneratingContainer } from './generating-container';
import { GeneratingText } from './generating-text';

const styles = cssMap({
	scroll: {
		maxHeight: '150px',
		paddingTop: token('space.200'),
		overflowY: 'auto',
		overflowX: 'hidden',
		scrollbarColor: `${token('color.border.bold')} transparent`,
	},
});

type AIAnswerLoadingDialogProps = {
	collapsed?: boolean;
	toggleCollapsedState?: () => void;
	callbacks?: {
		// called when card is rendered in the expanded state due to the answer being short
		onExpandedByDefault?: () => void;
		onExpandClicked?: () => void;
		onCollapseClicked?: () => void;
	};
};

// Used in quicksearch/advanced search
export const AIAnswerLoadingDialog = ({
	collapsed,
	toggleCollapsedState,
	callbacks,
}: AIAnswerLoadingDialogProps): React.JSX.Element => {
	const { commonAttributes, fireAnalyticsEvent } = useAIAnswerContext();

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<LegacySmartAnswerLoadingDialog
			collapsed={collapsed}
			toggleCollapsedState={toggleCollapsedState}
			callbacks={callbacks}
		/>
	);
};

const LegacySmartAnswerLoadingDialog = ({
	collapsed = true,
	toggleCollapsedState,
	callbacks,
}: AIAnswerLoadingDialogProps) => {
	const { partialResponse, queryReasoning, isReadingAids, onClose } = useAIAnswerContext();
	const { serveExperiment: serveSAINReasoningExperiment } = getFrontendExperiment({
		abTestId: 'kd_sain_reasoning_exp',
	});

	const isQuickSearch = Boolean(!isReadingAids && onClose);

	if (!partialResponse || partialResponse?.format !== NLPSearchResultFormat.MARKDOWN) {
		return (
			<AIAnswerSearchLoadingBorder isStreaming={false}>
				<GeneratingContainer>
					<GeneratingAnimation />
					<GeneratingText>
						{serveSAINReasoningExperiment && queryReasoning ? (
							queryReasoning
						) : (
							<FormattedMessage {...messages.ai_answer_generating_rovo} />
						)}
					</GeneratingText>
				</GeneratingContainer>
				<AIAnswerContentWarningAndBrandSection />
			</AIAnswerSearchLoadingBorder>
		);
	}

	if (isQuickSearch) {
		return (
			<AIAnswerSearchLoadingBorder isStreaming>
				<Suspense fallback={<Fragment />}>
					<SmartCardProvider>
						<Box xcss={styles.scroll}>
							<AIAnswerLoadingStream markdown={partialResponse.partialNlpResult} />
						</Box>
						<AIAnswerContentWarningAndBrandSection />
					</SmartCardProvider>
				</Suspense>
			</AIAnswerSearchLoadingBorder>
		);
	}

	return (
		<AIAnswerSearchLoadingBorder isStreaming>
			<Suspense fallback={<Fragment />}>
				<SmartCardProvider>
					<Collapse
						collapsed={collapsed}
						isLoading
						isStreaming
						expandWhenClickedAnywhere
						toggleCollapsedState={toggleCollapsedState}
						callbacks={callbacks}
					>
						<AIAnswerLoadingStream markdown={partialResponse.partialNlpResult} />
					</Collapse>
					<AIAnswerContentWarningAndBrandSection />
				</SmartCardProvider>
			</Suspense>
		</AIAnswerSearchLoadingBorder>
	);
};
